/**
 * FERP Portal - Subgraph Entity Browser (KISS Pattern)
 *
 * Renders a Tabulator data grid for a single entity type within a subgraph.
 * Uses the same UX pattern as primitive pages (TabulatorPrimitiveManager):
 *   - Row click → detail drawer (RecordDetailDrawer)
 *   - Create button → form modal (EntityFormGenerator)
 *   - Stat cards above the table
 *
 * NO page navigation — everything stays inline (drawer/modal).
 *
 * FERP Principles:
 * - Repository pattern: Data via useSubgraphEntityData hook
 * - Data-driven: All config from SubgraphEntityType
 * - KISS: Same UX pattern as primitive pages
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Stack,
  Group,
  Badge,
  Button,
  Alert,
  Text,
  Loader,
  Center,
  Modal,
  SimpleGrid,
} from '@mantine/core';
import { IconPlus, IconShieldCheck, IconRefresh, IconAlertTriangle } from '@tabler/icons-react';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { useSubgraphEntityData } from '@/hooks/useSubgraphEntityData';
import { EntityTabulatorTable } from './EntityTabulatorTable';
import { useViewMode } from '@/hooks/useViewMode';
import { ViewModeToggle } from '@/components/shared/ViewModeToggle';
import { EntityCardGrid, subgraphFieldAdapter } from '@/components/shared/EntityCardGrid';
import {
  RecordDetailDrawer,
  useRecordDrawer,
} from '@/components/RecordDetailDrawer';
import { InteractiveStatCard, type StatFilterScope } from '@/components/InteractiveStatCard';
import { EntityFormGenerator } from '@ferp/workflow-forms';
import type { UIContext } from '@ferp/workflow-forms';

export interface SubgraphEntityBrowserProps {
  subgraphId: string;
  entityType: SubgraphEntityType;
  /** DRR-103: Field names hidden by lens evaluation (Phase 3 setting rules) */
  hiddenFields?: string[];
  /** DRR-103 Phase 2: Entity IDs allowed by scope filtering. null = show all. */
  allowedEntityIds?: string[] | null;
}

export function SubgraphEntityBrowser({
  subgraphId,
  entityType,
  hiddenFields,
  allowedEntityIds,
}: Readonly<SubgraphEntityBrowserProps>): React.ReactElement {
  const { data: rawData, totalCount: rawCount, loading, error, refetch } = useSubgraphEntityData(subgraphId, entityType);
  const { viewMode, setViewMode } = useViewMode(`subgraph-${subgraphId}-${entityType.name}`);
  const cardAdapter = useMemo(() => subgraphFieldAdapter(entityType), [entityType]);

  // Detail drawer (KISS: same pattern as primitive pages)
  const drawer = useRecordDrawer<Record<string, unknown>>();

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Stat card filter state
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [activeScope, setActiveScope] = useState<StatFilterScope | null>(null);

  // DRR-103 Phase 2: Filter rows by scope-resolved entity IDs.
  const data = useMemo(() => {
    if (!allowedEntityIds) return rawData;
    const allowedSet = new Set(
      allowedEntityIds.map((id) => id.replace(/^[^:]+:[^:]+:/, '')),
    );
    const idField = entityType.identifierFields[0] ?? 'id';
    return rawData.filter((row) => {
      const rowId = String(row[idField] ?? row.id ?? '');
      return allowedSet.has(rowId);
    });
  }, [rawData, allowedEntityIds, entityType]);

  const totalCount = allowedEntityIds ? data.length : rawCount;

  // Count active items (status-based)
  const activeCount = useMemo(() => {
    return data.filter((row) => {
      const status = String(row.status ?? row.active ?? '').toLowerCase();
      return status === 'active' || status === 'true' || status === 'enabled';
    }).length;
  }, [data]);

  // Row click → open detail drawer
  const handleRowClick = useCallback(
    (row: Record<string, unknown>) => {
      drawer.open(row);
    },
    [drawer],
  );

  // Create modal context
  const createContext: UIContext = useMemo(() => ({
    mode: 'CREATE',
    tenantId: '',
    safetyLevel: entityType.isLifeCritical ? 'CRITICAL' : 'ELEVATED',
  }), [entityType.isLifeCritical]);

  // Stat card click handlers
  const handleStatClick = useCallback((filter: string, scope: StatFilterScope) => {
    if (activeFilter === filter && activeScope === scope) {
      setActiveFilter(null);
      setActiveScope(null);
    } else {
      setActiveFilter(filter);
      setActiveScope(scope);
    }
  }, [activeFilter, activeScope]);

  if (loading) {
    return (
      <Center h={300}>
        <Loader size="md" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title={`Failed to load ${entityType.pluralName}`}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Stat cards — same pattern as primitive pages */}
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
        <InteractiveStatCard
          label="Total"
          total={totalCount}
          newCount={0}
          color="cyan"
          isActive={activeFilter === 'total'}
          activeScope={activeFilter === 'total' ? activeScope : null}
          onClickAll={() => handleStatClick('total', 'all')}
          onClickNew={() => handleStatClick('total', 'new')}
        />
        <InteractiveStatCard
          label="Active"
          total={activeCount}
          newCount={0}
          color="green"
          isActive={activeFilter === 'active'}
          activeScope={activeFilter === 'active' ? activeScope : null}
          onClickAll={() => handleStatClick('active', 'all')}
          onClickNew={() => handleStatClick('active', 'new')}
        />
      </SimpleGrid>

      {/* Entity header */}
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={600} size="lg">{entityType.pluralName}</Text>
          <Badge variant="light" color="gray" size="sm">{totalCount}</Badge>
          {entityType.isLifeCritical && (
            <Badge color="red" variant="filled" size="sm">Life-Critical</Badge>
          )}
          {entityType.complianceFrameworks.map((fw) => (
            <Badge key={fw} size="xs" variant="outline" color="gray">{fw}</Badge>
          ))}
        </Group>
        <Group gap="xs">
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setCreateModalOpen(true)}
          >
            New {entityType.name}
          </Button>
        </Group>
      </Group>

      {/* Life-critical warning */}
      {entityType.isLifeCritical && (
        <Alert
          icon={<IconShieldCheck size={16} />}
          color="red"
          variant="light"
          title="Life-Critical Entity"
        >
          All operations on {entityType.pluralName} are logged for compliance.
          Dual approval may be required for sensitive actions.
        </Alert>
      )}

      {/* Entity data: table or card view */}
      {viewMode === 'card' ? (
        <EntityCardGrid
          items={data}
          fieldAdapter={cardAdapter}
          onItemClick={handleRowClick}
          getId={(item) => String(item[entityType.identifierFields[0] ?? 'id'] ?? item.id ?? '')}
          emptyMessage={`No ${entityType.pluralName.toLowerCase()} found`}
        />
      ) : (
        <EntityTabulatorTable
          entityType={entityType}
          data={data}
          hiddenFields={hiddenFields}
          onRowClick={handleRowClick}
          exportFileName={`${subgraphId}_${entityType.name.toLowerCase()}`}
        />
      )}

      {/* Detail drawer — KISS: same as primitive pages */}
      <RecordDetailDrawer
        selectedRecord={drawer.selectedRecord}
        opened={drawer.opened}
        close={drawer.close}
        title={`${entityType.name} Detail`}
      />

      {/* Create modal — uses EntityFormGenerator (data-driven, DRR-055) */}
      <Modal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title={`Create ${entityType.name}`}
        size="lg"
      >
        <EntityFormGenerator
          subgraph={subgraphId}
          entityType={entityType.graphqlType}
          context={createContext}
          onSubmit={(values) => {
            // TODO: Wire to backend mutation when available
            console.info(`[SubgraphEntityBrowser] Create ${entityType.graphqlType}:`, values);
            setCreateModalOpen(false);
            refetch();
          }}
        />
      </Modal>
    </Stack>
  );
}

export default SubgraphEntityBrowser;
